import {ref, computed} from 'vue';
import {defineStore} from 'pinia';
import {createError, navigateTo, useLogger, useTranslation, useUniversalPost} from '#imports';
import {readonlyState} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {InvitationContextResponse, BelongsToCompany, PartnershipHighlights, InvitationFreelancers} from '@api';
import {ClientSignupApi, ClientSignupInvitationContextApi, FetchError, InvitationType} from '@api';
import {apiConfiguration} from '~/configuration/apiConfiguration';

export const useLandingInvitationStore = defineStore('landing-invitation-store', () => {
    const logger = useLogger();
    const {t} = useTranslation();

    const id = ref<string>();
    const idType = ref<number>();

    function init(anId: string, anIdType: number) {
        id.value = anId;
        idType.value = anIdType;
    }

    const type = computed(() => (idType.value !== undefined ? typeCodeToEnum(idType.value) : undefined));
    function typeCodeToEnum(idType: number): InvitationType {
        if (idType === 0) {
            return InvitationType.Organization;
        }
        if (idType === 1) {
            return InvitationType.PersonalInvitation;
        }
        if (idType === 2) {
            return InvitationType.ShareableLink;
        }
        throw createError({
            statusCode: 400,
            message: 'Invalid invitation type',
        });
    }

    const pageSubCategoryTrackingProperty = computed(() => {
        if (type.value === undefined) {
            return undefined;
        }
        if (type.value === InvitationType.Organization) {
            return 'organization-link-invitation-page';
        }
        if (type.value === InvitationType.PersonalInvitation) {
            return 'direct-invitation-page';
        }
        if (type.value === InvitationType.ShareableLink) {
            return 'company-link-invitation-page';
        }
        return undefined;
    });

    const clientInvitationType = computed(() => {
        if (type.value === undefined) {
            return undefined;
        }
        if (type.value === InvitationType.Organization) {
            return 'organization_link';
        }
        if (type.value === InvitationType.PersonalInvitation) {
            return 'company_email';
        }
        if (type.value === InvitationType.ShareableLink) {
            return 'company_link';
        }
        return undefined;
    });

    const invitationApi = new ClientSignupInvitationContextApi(apiConfiguration);
    const landingInvitation = ref<InvitationContextResponse>();

    function belongsToCompany(): Promise<BelongsToCompany> {
        return invitationApi.belongsToCompany({
            invitationCode: id.value!,
            invitationType: type.value!,
        });
    }

    async function getLandingInvitation(): Promise<InvitationContextResponse> {
        return await invitationApi.getInvitationContext({
            invitationCode: id.value!,
            invitationType: type.value!,
        });
    }
    function setLandingInvitation(aLandingInvitation: InvitationContextResponse) {
        landingInvitation.value = aLandingInvitation;
    }

    const signupApi = new ClientSignupApi(apiConfiguration);
    async function joinTeam() {
        const ssoJitSignInUrl = landingInvitation.value?.ssoJitSignInUrl;
        if (ssoJitSignInUrl) {
            await navigateTo(ssoJitSignInUrl, {external: true});
            return;
        }

        try {
            await signupApi.initSignupForInvitation({
                signupInitializationRequestForInvitation: {
                    invitationCode: id.value!,
                    invitationType: type.value!,
                    invitationPageUrl: withQueryParam(window.location.href, 'autoSubmit', 'true'),
                },
            });
            await navigateTo('/signup-client/?pageSource=client_invitations', {external: true});
        } catch (e) {
            if (e instanceof FetchError && e.statusCode === 400) {
                pushVJoySnackbar({props: {message: e.getValidationErrors(t).email[0], level: 'error'}});
            } else {
                pushVJoySnackbar({props: {message: t('signup.client.server.error'), level: 'error'}});
            }
            logger.error(e);
            return {data: null, error: e as FetchError};
        }
    }

    function withQueryParam(url: string, key: string, value: string): string {
        const newUrl = new URL(url);
        newUrl.searchParams.set(key, value);
        return newUrl.toString();
    }

    async function getFreelancers(): Promise<InvitationFreelancers> {
        return await invitationApi.getFreelancersForInvitation({
            invitationCode: id.value!,
            invitationType: type.value!,
        });
    }

    async function getPartnershipHighlights(): Promise<PartnershipHighlights> {
        return await invitationApi.getPartnershipHighlights({
            invitationCode: id.value!,
            invitationType: type.value!,
        });
    }

    return {
        // RO state a-z
        clientInvitationType: readonlyState(clientInvitationType),
        landingInvitation: readonlyState(landingInvitation),
        pageSubCategoryTrackingProperty,

        // function a-z
        belongsToCompany,
        getFreelancers,
        getLandingInvitation,
        getPartnershipHighlights,
        init,
        joinTeam,
        setLandingInvitation,
    };
});
